import { Box, MessageBlock } from '@deckee/deck-hand';
import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import { trackWeatherForecastNearbyStationSelected } from '../../helpers/analytics/weatherForecastAnalytics';
import type { NearbyWeatherStationQueryType } from '../../helpers/location/nearbyWeatherStations';
import { getWeatherForecastHomePath } from '../../helpers/location/searchPaths';
import { useNearbyWeatherStations } from '../../hooks/useNearbyWeatherStations';
import NearestForecastMessage from './NearestForecastMessage';

export interface GraphCardNoDataProps {
  chartLabel: string;
  stationType: NearbyWeatherStationQueryType;
  isLocationMissingData: boolean;
  latitude: number;
  longitude: number;
  selectedDate: string;
  locationName?: string;
}

const DayEmptyState: FC<{ chartLabel: string }> = ({ chartLabel }) => (
  <MessageBlock type="warning" size="small">
    {`No ${chartLabel
      .toLowerCase()
      // Replace any lowercase "uv" with "UV"
      .replace(/\buv\b/g, 'UV')} data available for this day.`}
  </MessageBlock>
);

const GraphCardNoData: FC<GraphCardNoDataProps> = ({
  chartLabel,
  isLocationMissingData,
  latitude,
  longitude,
  stationType,
  selectedDate,
  locationName,
}) => {
  const history = useHistory();
  const { stations, referenceLocationName, isLoading } =
    useNearbyWeatherStations(
      latitude,
      longitude,
      stationType,
      isLocationMissingData,
    );

  const nearestStation = stations[0];
  const resolvedLocationName =
    locationName || referenceLocationName || 'this location';

  if (!isLocationMissingData) {
    return <DayEmptyState chartLabel={chartLabel} />;
  }

  if (isLoading) {
    return (
      <Box
        data-testid={`weather-forecast-${chartLabel.toLowerCase()}-nearby-loading`}
        aria-busy
      >
        <Shimmer on>
          <Box minHeight="80px" />
        </Shimmer>
      </Box>
    );
  }

  if (!nearestStation || nearestStation.distanceKm == null) {
    return (
      <MessageBlock type="warning" size="small">
        {`No ${chartLabel.toLowerCase()} data available for this location.`}
      </MessageBlock>
    );
  }

  const handleNearestStationClick = () => {
    trackWeatherForecastNearbyStationSelected({
      chartLabel,
      stationType,
      latitude: nearestStation.latitude,
      longitude: nearestStation.longitude,
      selectedDate,
      stationName: nearestStation.name,
    });

    history.push(
      getWeatherForecastHomePath(
        nearestStation.latitude,
        nearestStation.longitude,
        selectedDate,
      ),
    );
  };

  return (
    <NearestForecastMessage
      chartLabel={chartLabel}
      locationName={resolvedLocationName}
      nearestStationName={nearestStation.shortName}
      distanceKm={nearestStation.distanceKm}
      onNearestStationClick={handleNearestStationClick}
    />
  );
};

export default GraphCardNoData;
